/**
 * 系统设置 - 会员价格 / 试用 / 短信 / 抓取 / 联系方式 配置
 * 接 /api/admin/settings(kv 存储)
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Crown, Clock, MessageSquare, Database, Save, RotateCcw, Info, AlertTriangle,
  Phone, Mail, MessageCircle, Globe, Upload, X, Image as ImageIcon,
  Briefcase, Headphones, Users, Shield, Key, RefreshCw, Copy, Archive,
  CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

// 默认配置(跟代码里 TIERS 同步)
const DEFAULTS = {
  tiers: {
    'Pro 月付': { price: 169, duration: 30, color: 'cyan' },
    'Pro 年付': { price: 699, duration: 365, color: 'purple' },
    'MAX 买断': { price: 1999, duration: null, color: 'gold' },
  },
  trial: {
    hours: 24,
    autoGrant: true,
  },
  sms: {
    dailyLimit: 10,
    codeExpiry: 5,
    provider: 'tencent',
  },
  fetch: {
    cron: '0 14 * * *',
    sources: ['cwl', 'ssqzj'],
  },
};

export function SettingsView() {
  const [config, setConfig] = useState(DEFAULTS);
  // 联系方式(完整版,所有字段都可在后台配置)
  const [contact, setContact] = useState({
    // 微信群(主要联系方式)
    wechat_group_label: '微信群',
    wechat_group_desc: '扫码加入用户群',
    wechat_group_qr: '',       // 二维码(data URL 或 http URL)
    wechat_group_id: '',        // 群号/微信号
    // 商务合作
    business_label: '商务合作',
    business_email: 'bd@3dmax.com',
    business_wechat: '',         // 商务微信号
    business_qr: '',             // 商务微信二维码
    business_desc: '渠道 / 推广 / 媒体合作',
    // 公众号 / 视频号(可选)
    official_account_label: '官方公众号',
    official_account_id: '',
    official_account_qr: '',
    video_account_label: '视频号',
    video_account_id: '',
    // 客服
    support_email: 'support@3dmax.com',
    support_phone: '',
    support_work_hours: '工作日 9:00-18:00',
  });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // 启动时从后端加载
  useEffect(() => {
    (async () => {
      try {
        const r = await api.admin.settings();
        const s = r.settings || {};
        // 填到 contact
        const newContact = { ...contact };
        for (const k of Object.keys(contact)) {
          if (s[k]?.value !== undefined) newContact[k] = s[k].value;
        }
        setContact(newContact);
        // 填到 config(tiers / trial / sms)
        if (s.tier_pro_monthly_price) setConfig(c => ({ ...c, tiers: { ...c.tiers, 'Pro 月付': { ...c.tiers['Pro 月付'], price: +s.tier_pro_monthly_price.value } } }));
        if (s.tier_pro_yearly_price) setConfig(c => ({ ...c, tiers: { ...c.tiers, 'Pro 年付': { ...c.tiers['Pro 年付'], price: +s.tier_pro_yearly_price.value } } }));
        if (s.tier_max_price) setConfig(c => ({ ...c, tiers: { ...c.tiers, 'MAX 买断': { ...c.tiers['MAX 买断'], price: +s.tier_max_price.value } } }));
        if (s.trial_hours) setConfig(c => ({ ...c, trial: { ...c.trial, hours: +s.trial_hours.value } }));
      } catch (e) {
        console.warn('加载设置失败:', e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function save() {
    setLoading(true);
    try {
      // 1. 保存联系方式
      const contactUpdates = Object.entries(contact).map(([key, value]) => ({ key, value }));
      await api.admin.updateSettings(contactUpdates);
      // 2. 保存价格 / 试用
      const tierUpdates = [
        { key: 'tier_pro_monthly_price', value: String(config.tiers['Pro 月付'].price) },
        { key: 'tier_pro_yearly_price', value: String(config.tiers['Pro 年付'].price) },
        { key: 'tier_max_price', value: String(config.tiers['MAX 买断'].price) },
        { key: 'trial_hours', value: String(config.trial.hours) },
      ];
      await api.admin.updateSettings(tierUpdates);
      setSaved(true);
      toast.success('设置已保存到云端');
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      toast.error('保存失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    if (!confirm('重置为默认设置?')) return;
    setConfig(DEFAULTS);
    toast.success('已重置');
  }

  return (
    <div className="space-y-2 max-w-3xl">
      {/* 提示 */}
      <div className="card !p-2.5 bg-brand-gold/5 border-brand-gold/20 flex items-start gap-2">
        <Info size={14} className="text-brand-gold flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-ink-2">
          <div className="font-medium text-brand-gold mb-0.5">关于设置</div>
          <div>当前版本配置仅前端展示,后端 <code className="text-brand-cyan">/api/admin/settings</code> 端点待添加。</div>
          <div className="mt-0.5">价格 / 试用时长的实际值以代码中 <code className="text-brand-cyan">TIERS</code> 常量为准。</div>
        </div>
      </div>

      {/* 会员档 */}
      <ConfigSection icon={Crown} title="会员档配置" desc="月付 / 年付 / 买断的价格与时长">
        <div className="space-y-1.5">
          {Object.entries(config.tiers).map(([name, t]) => (
            <div key={name} className="flex items-center gap-2 p-2 bg-bg-base rounded-md">
              <div className={clsx(
                'w-1.5 h-6 rounded-full',
                t.color === 'gold' ? 'bg-brand-gold' :
                t.color === 'purple' ? 'bg-brand-purple' : 'bg-brand-cyan'
              )} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium">{name}</div>
                <div className="text-[9px] text-ink-3">
                  {t.duration ? `${t.duration} 天` : '永久'}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-ink-3">¥</span>
                <input
                  type="number"
                  value={t.price}
                  onChange={e => setConfig(c => ({
                    ...c, tiers: { ...c.tiers, [name]: { ...t, price: +e.target.value } }
                  }))}
                  className="w-16 px-1.5 py-0.5 bg-bg-card border border-white/10 rounded text-xs font-mono text-right"
                />
              </div>
            </div>
          ))}
        </div>
      </ConfigSection>

      {/* 试用设置 */}
      <ConfigSection icon={Clock} title="试用设置" desc="新用户自动获得的试用时长">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs">自动试用</div>
              <div className="text-[10px] text-ink-3">新用户注册时自动获得</div>
            </div>
            <Toggle
              checked={config.trial.autoGrant}
              onChange={v => setConfig(c => ({ ...c, trial: { ...c.trial, autoGrant: v } }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs">试用时长</div>
              <div className="text-[10px] text-ink-3">多少小时后过期</div>
            </div>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={config.trial.hours}
                onChange={e => setConfig(c => ({ ...c, trial: { ...c.trial, hours: +e.target.value } }))}
                className="w-14 px-1.5 py-0.5 bg-bg-base border border-white/10 rounded text-xs font-mono text-right"
              />
              <span className="text-[10px] text-ink-3">小时</span>
            </div>
          </div>
        </div>
      </ConfigSection>

      {/* 短信 */}
      <ConfigSection icon={MessageSquare} title="短信配置" desc="验证码发送的限流与有效期">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs">短信服务商</div>
              <div className="text-[10px] text-ink-3">腾讯云 / 阿里云</div>
            </div>
            <select
              value={config.sms.provider}
              onChange={e => setConfig(c => ({ ...c, sms: { ...c.sms, provider: e.target.value } }))}
              className="bg-bg-base border border-white/10 rounded px-1.5 py-0.5 text-xs"
            >
              <option value="tencent">腾讯云</option>
              <option value="aliyun">阿里云</option>
              <option value="dev">开发模式(本地)</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs">每日发送上限</div>
              <div className="text-[10px] text-ink-3">单手机号每天最多条数</div>
            </div>
            <input
              type="number"
              value={config.sms.dailyLimit}
              onChange={e => setConfig(c => ({ ...c, sms: { ...c.sms, dailyLimit: +e.target.value } }))}
              className="w-14 px-1.5 py-0.5 bg-bg-base border border-white/10 rounded text-xs font-mono text-right"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs">验证码有效期</div>
              <div className="text-[10px] text-ink-3">多少分钟后失效</div>
            </div>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={config.sms.codeExpiry}
                onChange={e => setConfig(c => ({ ...c, sms: { ...c.sms, codeExpiry: +e.target.value } }))}
                className="w-14 px-1.5 py-0.5 bg-bg-base border border-white/10 rounded text-xs font-mono text-right"
              />
              <span className="text-[10px] text-ink-3">分钟</span>
            </div>
          </div>
        </div>
      </ConfigSection>

      {/* 抓取 */}
      <ConfigSection icon={Database} title="数据抓取" desc="自动抓取的频率与数据源">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs">Cron 表达式</div>
              <div className="text-[10px] text-ink-3 font-mono">UTC 时间</div>
            </div>
            <input
              type="text"
              value={config.fetch.cron}
              onChange={e => setConfig(c => ({ ...c, fetch: { ...c.fetch, cron: e.target.value } }))}
              className="w-28 px-1.5 py-0.5 bg-bg-base border border-white/10 rounded text-xs font-mono"
            />
          </div>
          <div>
            <div className="text-xs mb-1">数据源</div>
            <div className="flex flex-wrap gap-1">
              {['cwl', 'ssqzj', '17500'].map(src => {
                const checked = config.fetch.sources.includes(src);
                return (
                  <button
                    key={src}
                    onClick={() => setConfig(c => ({
                      ...c, fetch: {
                        ...c.fetch,
                        sources: checked
                          ? c.fetch.sources.filter(s => s !== src)
                          : [...c.fetch.sources, src]
                      }
                    }))}
                    className={clsx(
                      'px-2 py-0.5 rounded text-[11px] border transition',
                      checked
                        ? 'border-brand-cyan bg-brand-cyan/10 text-brand-cyan'
                        : 'border-white/10 text-ink-3'
                    )}
                  >
                    {src}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </ConfigSection>

      {/* 联系方式 — 完整版(微信群 / 商务合作 / 公众号 / 视频号 / 客服) */}
      <ConfigSection icon={Phone} title="联系方式" desc="用户端「联系我们」展示的内容 — 上传二维码图片,所有信息可改">
        <div className="space-y-3">
          {/* === 1. 微信群(主要) === */}
          <ContactGroup icon={Users} title="微信群" color="cyan">
            <Row label="名称" value={contact.wechat_group_label} onChange={v => setContact(c => ({ ...c, wechat_group_label: v }))} placeholder="例:3D MAX 用户群" />
            <Row label="描述" value={contact.wechat_group_desc} onChange={v => setContact(c => ({ ...c, wechat_group_desc: v }))} placeholder="例:扫码加入用户群" />
            <Row label="群号/微信号" value={contact.wechat_group_id} onChange={v => setContact(c => ({ ...c, wechat_group_id: v }))} placeholder="选填,用户可手动加群" />
            <ImageUpload label="群二维码" value={contact.wechat_group_qr} onChange={v => setContact(c => ({ ...c, wechat_group_qr: v }))} hint="建议 300x300 PNG/JPG" />
          </ContactGroup>

          {/* === 2. 商务合作 === */}
          <ContactGroup icon={Briefcase} title="商务合作" color="gold">
            <Row label="名称" value={contact.business_label} onChange={v => setContact(c => ({ ...c, business_label: v }))} placeholder="例:商务合作" />
            <Row label="邮箱" value={contact.business_email} onChange={v => setContact(c => ({ ...c, business_email: v }))} placeholder="bd@yourdomain.com" />
            <Row label="微信号" value={contact.business_wechat} onChange={v => setContact(c => ({ ...c, business_wechat: v }))} placeholder="选填" />
            <ImageUpload label="商务微信二维码" value={contact.business_qr} onChange={v => setContact(c => ({ ...c, business_qr: v }))} hint="选填,让用户加商务微信" />
            <Row label="描述" value={contact.business_desc} onChange={v => setContact(c => ({ ...c, business_desc: v }))} placeholder="例:渠道 / 推广 / 媒体合作" />
          </ContactGroup>

          {/* === 3. 公众号 / 视频号(可选) === */}
          <ContactGroup icon={Globe} title="官方账号(选填)" color="purple">
            <Row label="公众号名称" value={contact.official_account_label} onChange={v => setContact(c => ({ ...c, official_account_label: v }))} placeholder="例:3D MAX 研究院" />
            <Row label="公众号 ID" value={contact.official_account_id} onChange={v => setContact(c => ({ ...c, official_account_id: v }))} placeholder="选填" />
            <ImageUpload label="公众号二维码" value={contact.official_account_qr} onChange={v => setContact(c => ({ ...c, official_account_qr: v }))} hint="选填" />
            <div className="border-t border-white/5 my-1" />
            <Row label="视频号名称" value={contact.video_account_label} onChange={v => setContact(c => ({ ...c, video_account_label: v }))} placeholder="例:3D MAX 视频号" />
            <Row label="视频号 ID" value={contact.video_account_id} onChange={v => setContact(c => ({ ...c, video_account_id: v }))} placeholder="选填" />
          </ContactGroup>

          {/* === 4. 客服 === */}
          <ContactGroup icon={Headphones} title="客服" color="rose">
            <Row label="客服邮箱" value={contact.support_email} onChange={v => setContact(c => ({ ...c, support_email: v }))} />
            <Row label="客服电话" value={contact.support_phone} onChange={v => setContact(c => ({ ...c, support_phone: v }))} placeholder="选填" />
            <Row label="工作时间" value={contact.support_work_hours} onChange={v => setContact(c => ({ ...c, support_work_hours: v }))} />
          </ContactGroup>

          {/* 实时预览 */}
          <div className="border-t border-white/10 pt-3 mt-3">
            <div className="text-[10px] text-ink-3 mb-2 flex items-center gap-1">
              <Info size={10} /> 用户端预览(实时更新)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* 微信群预览 */}
              {contact.wechat_group_label && (
                <ContactPreview
                  qr={contact.wechat_group_qr}
                  title={contact.wechat_group_label}
                  desc={contact.wechat_group_desc || '扫码加入用户群'}
                  color="text-brand-cyan"
                />
              )}
              {/* 商务合作预览 */}
              {(contact.business_label || contact.business_email) && (
                <ContactPreview
                  qr={contact.business_qr}
                  title={contact.business_label || '商务合作'}
                  desc={contact.business_email || 'bd@3dmax.com'}
                  color="text-brand-gold"
                />
              )}
              {/* 公众号预览 */}
              {contact.official_account_label && (
                <ContactPreview
                  qr={contact.official_account_qr}
                  title={contact.official_account_label}
                  desc={contact.official_account_id || '关注公众号'}
                  color="text-brand-purple"
                />
              )}
              {/* 视频号预览 */}
              {contact.video_account_label && (
                <ContactPreview
                  qr=""
                  title={contact.video_account_label}
                  desc={contact.video_account_id || '微信视频号'}
                  color="text-brand-rose"
                />
              )}
            </div>
          </div>
        </div>
      </ConfigSection>

      {/* 安全 - Token 管理 */}
      <SecuritySection />

      {/* 数据备份 */}
      <PaymentSection />

      <BackupSection />

      {/* 底部操作 */}
      <div className="flex items-center gap-1.5 pt-1">
        <button
          onClick={save}
          className={clsx(
            'flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition',
            saved
              ? 'bg-brand-cyan/20 text-brand-cyan'
              : 'bg-gradient-to-r from-brand-purple to-brand-cyan text-white hover:opacity-90'
          )}
        >
          <Save size={12} /> {saved ? '已保存' : '保存设置'}
        </button>
        <button
          onClick={reset}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs text-ink-3 hover:bg-white/5"
        >
          <RotateCcw size={12} /> 重置
        </button>
      </div>
    </div>
  );
}

function ConfigSection({ icon: Icon, title, desc, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="card !p-3"
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0">
          <Icon size={12} className="text-brand-cyan" />
        </div>
        <div>
          <h3 className="text-xs font-semibold">{title}</h3>
          <p className="text-[10px] text-ink-3 mt-0.5">{desc}</p>
        </div>
      </div>
      {children}
    </motion.div>
  );
}

function Row({ label, value, onChange, placeholder = '', type = 'text' }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-xs text-ink-2 w-28 flex-shrink-0">{label}</div>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="flex-1 px-2 py-1 bg-bg-base border border-white/10 rounded text-xs outline-none focus:border-brand-cyan/40"
      />
    </div>
  );
}

// 联系方式分组(带标题 + 图标)
function ContactGroup({ icon: Icon, title, color = 'cyan', children }) {
  const colorMap = {
    cyan: 'text-brand-cyan bg-brand-cyan/10 border-brand-cyan/30',
    gold: 'text-brand-gold bg-brand-gold/10 border-brand-gold/30',
    purple: 'text-brand-purple bg-brand-purple/10 border-brand-purple/30',
    rose: 'text-brand-rose bg-brand-rose/10 border-brand-rose/30',
  };
  return (
    <div className="p-2.5 rounded-lg bg-bg-base/50 border border-white/5">
      <div className="flex items-center gap-1.5 mb-2">
        <div className={clsx('w-5 h-5 rounded flex items-center justify-center border', colorMap[color])}>
          <Icon size={11} />
        </div>
        <span className="text-[11px] font-semibold">{title}</span>
      </div>
      <div className="space-y-1.5 pl-6">
        {children}
      </div>
    </div>
  );
}

// 图片上传组件(支持点击上传 + 预览 + 清除)
function ImageUpload({ label, value, onChange, hint = '' }) {
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error('图片不能超过 500KB,请压缩后再传');
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      onChange(evt.target.result);  // data:image/...;base64,...
      toast.success('已上传');
    };
    reader.readAsDataURL(file);
  };

  const isImage = value && (value.startsWith('data:image') || value.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i));

  return (
    <div>
      <div className="text-xs text-ink-2 mb-1">{label}</div>
      <div className="flex items-start gap-2">
        {/* 预览 */}
        <div className="w-20 h-20 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
          {isImage ? (
            <img src={value} alt={label} className="w-full h-full object-contain" />
          ) : (
            <ImageIcon size={20} className="text-ink-3" />
          )}
        </div>
        {/* 操作按钮 */}
        <div className="flex-1 flex flex-col gap-1.5">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-2 py-1 rounded text-[10px] bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan/20 flex items-center gap-1"
            >
              <Upload size={10} /> 上传图片
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="px-2 py-1 rounded text-[10px] bg-accent-danger/10 border border-accent-danger/30 text-accent-danger hover:bg-accent-danger/20 flex items-center gap-1"
              >
                <X size={10} /> 清除
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />
          <input
            type="text"
            value={value?.startsWith('data:') ? '(已上传,共 ' + Math.round(value.length / 1024) + ' KB)' : value}
            onChange={e => onChange(e.target.value)}
            placeholder="或粘贴图片 URL"
            className="w-full px-2 py-1 bg-bg-base border border-white/10 rounded text-[10px] outline-none focus:border-brand-cyan/40"
          />
          {hint && <div className="text-[9px] text-ink-3">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

// 联系方式预览卡片
function ContactPreview({ qr, title, desc, color }) {
  const isImage = qr && (qr.startsWith('data:image') || qr.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i));
  return (
    <div className="rounded-xl bg-white/5 p-3 flex items-center gap-3">
      <div className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
        {isImage ? (
          <img src={qr} alt={title} className="w-full h-full object-contain" />
        ) : (
          <div className="text-2xl">👥</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={clsx('text-sm font-bold truncate', color)}>{title}</div>
        <div className="text-xs text-ink-2 mt-0.5 truncate">{desc}</div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative w-8 h-4 rounded-full transition',
        checked ? 'bg-brand-cyan' : 'bg-white/10'
      )}
    >
      <div className={clsx(
        'absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform',
        checked ? 'translate-x-4' : 'translate-x-0.5'
      )} />
    </button>
  );
}

// ===========================
// 安全 - Token 管理
// ===========================
function SecuritySection() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.admin.token.me();
      if (r?.ok) setMe(r.admin);
    } catch (e) { toast.error('加载失败'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleRotate = async () => {
    if (confirmText !== 'ROTATE') {
      toast.error('请先输入确认码 ROTATE');
      return;
    }
    try {
      const r = await api.admin.token.rotate({ confirm: 'ROTATE' });
      if (r?.ok) {
        toast.success('Token 已轮换,旧 token 立即失效');
        setShowToken(r.new_token);
        setConfirmText('');
        load();
      } else {
        toast.error(r?.error || '轮换失败');
      }
    } catch (e) { toast.error('轮换失败: ' + e.message); }
  };

  const handleRevoke = async () => {
    if (!confirm('确认撤销当前 Token?\n\n撤销后:\n• 当前会话立即失效\n• 需要重新登录生成新 Token\n• 建议同时更换生产 token')) return;
    try {
      const r = await api.admin.token.revoke();
      if (r?.ok) {
        toast.success(r.message);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(r?.error || '撤销失败');
      }
    } catch (e) { toast.error('撤销失败: ' + e.message); }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success('已复制');
  };

  return (
    <div className="card !p-3">
      <div className="flex items-start gap-2 mb-3">
        <div className="w-6 h-6 rounded-md bg-brand-rose/10 flex items-center justify-center flex-shrink-0">
          <Shield size={12} className="text-brand-rose" />
        </div>
        <div>
          <h3 className="text-xs font-semibold flex items-center gap-1">
            安全 · Admin Token
            <span className="text-[9px] text-amber-400 bg-amber-400/10 px-1 rounded">生产级</span>
          </h3>
          <p className="text-[10px] text-ink-3 mt-0.5">轮换/撤销当前 admin token · 90 天自动续期</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4 text-ink-3 text-xs">加载中...</div>
      ) : me ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div className="bg-bg-elev/50 rounded p-2">
              <div className="text-ink-3">状态</div>
              <div className={clsx('font-semibold', me.token_active ? 'text-brand-cyan' : 'text-red-400')}>
                {me.token_active ? '✓ 有效' : '✗ 已撤销'}
              </div>
            </div>
            <div className="bg-bg-elev/50 rounded p-2">
              <div className="text-ink-3">剩余天数</div>
              <div className={clsx('font-semibold', me.remaining_days > 14 ? 'text-ink-1' : me.remaining_days > 0 ? 'text-amber-400' : 'text-red-400')}>
                {me.remaining_days > 0 ? `${me.remaining_days} 天` : '已过期'}
              </div>
            </div>
            <div className="bg-bg-elev/50 rounded p-2">
              <div className="text-ink-3">上次使用</div>
              <div className="font-mono text-ink-1">
                {me.token_last_used_at ? new Date(me.token_last_used_at).toLocaleString('zh-CN') : '从未'}
              </div>
            </div>
            <div className="bg-bg-elev/50 rounded p-2">
              <div className="text-ink-3">到期时间</div>
              <div className="font-mono text-ink-1">
                {me.token_expires_at ? new Date(me.token_expires_at).toLocaleString('zh-CN') : '-'}
              </div>
            </div>
          </div>

          {showToken && (
            <div className="bg-brand-cyan/5 border border-brand-cyan/30 rounded p-2 mt-2">
              <div className="text-[10px] text-ink-2 mb-1 flex items-center gap-1">
                <Key size={10} /> 新 Token(只显示一次)
              </div>
              <div className="flex items-center gap-1">
                <code className="flex-1 bg-bg-base px-2 py-1.5 rounded text-[10px] font-mono break-all text-brand-cyan">{showToken}</code>
                <button onClick={() => copy(showToken)} className="p-1.5 rounded bg-brand-cyan/20 text-brand-cyan hover:bg-brand-cyan/30">
                  <Copy size={10} />
                </button>
              </div>
              <div className="text-[10px] text-amber-400 mt-1">⚠️ 关闭后无法再次查看完整 token,务必复制保存</div>
            </div>
          )}

          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="确认码:ROTATE"
              className="flex-1 px-2 py-1.5 bg-bg-base border border-white/10 rounded text-xs outline-none focus:border-brand-cyan/40 font-mono"
            />
            <button
              onClick={handleRotate}
              disabled={confirmText !== 'ROTATE'}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs bg-brand-cyan/20 text-brand-cyan hover:bg-brand-cyan/30 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <RefreshCw size={11} /> 轮换
            </button>
            <button
              onClick={handleRevoke}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs text-red-400 hover:bg-red-400/10"
            >
              <X size={11} /> 撤销
            </button>
          </div>
          <div className="text-[10px] text-ink-3 mt-1">轮换后旧 token 立即失效,需更新前端 API URL 中的 _t 参数</div>
        </div>
      ) : (
        <div className="text-center py-4 text-ink-3 text-xs">无数据</div>
      )}
    </div>
  );
}

// ===========================
// 数据备份
// ===========================
function BackupSection() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.admin.backup.list();
      if (r?.ok) setList(r.items || []);
    } catch (e) { toast.error('加载失败'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!confirm('确认立即创建一次完整数据备份?\n\n将备份 12 张主表(ssq_periods / users / payments / favorites / ...)\n存储到 D1 `backups` 表')) return;
    setCreating(true);
    try {
      const r = await api.admin.backup.create();
      if (r?.ok) {
        toast.success(`备份已创建:${r.key} (${(r.size/1024).toFixed(1)}KB, ${r.rows} 行)`);
        load();
      } else {
        toast.error(r?.error || '创建失败');
      }
    } catch (e) { toast.error('创建失败: ' + e.message); }
    setCreating(false);
  };

  const handleRestore = async (key) => {
    if (!confirm(`⚠️ 危险操作:从备份 ${key} 恢复数据\n\n将覆盖现有数据!\n确定吗?`)) return;
    setRestoring(key);
    try {
      const r = await api.admin.backup.restore({ key, confirm: 'YES_RESTORE' });
      if (r?.ok) {
        toast.success(`恢复完成: ${JSON.stringify(r.results.restored)}`);
        load();
      } else {
        toast.error(r?.error || '恢复失败');
      }
    } catch (e) { toast.error('恢复失败: ' + e.message); }
    setRestoring(null);
  };

  const handleCleanup = async () => {
    if (!confirm('确认清理 30 天前的过期备份?')) return;
    try {
      const r = await api.admin.backup.cleanup();
      if (r?.ok) {
        toast.success(`清理完成:删除 ${r.deleted} 个过期备份,剩余 ${r.total}`);
        load();
      }
    } catch (e) { toast.error('清理失败'); }
  };

  return (
    <div className="card !p-3">
      <div className="flex items-start gap-2 mb-3">
        <div className="w-6 h-6 rounded-md bg-brand-purple/10 flex items-center justify-center flex-shrink-0">
          <Archive size={12} className="text-brand-purple" />
        </div>
        <div className="flex-1">
          <h3 className="text-xs font-semibold">数据备份</h3>
          <p className="text-[10px] text-ink-3 mt-0.5">D1 自动每日备份 · 保留 30 天 · 危险表(用户/支付/提现)只增不覆盖</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-[10px] text-ink-3 hover:text-brand-cyan flex items-center gap-0.5"
        >
          <RefreshCw size={10} className={loading ? 'animate-spin' : ''} /> 刷新
        </button>
      </div>

      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <button
          onClick={handleCreate}
          disabled={creating}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs bg-brand-cyan/20 text-brand-cyan hover:bg-brand-cyan/30 disabled:opacity-50"
        >
          {creating ? <RefreshCw size={11} className="animate-spin" /> : <Archive size={11} />}
          立即备份
        </button>
        <button
          onClick={handleCleanup}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs text-ink-3 hover:text-amber-400 border border-white/5 hover:border-amber-400/30"
        >
          清理过期
        </button>
        <span className="text-[10px] text-ink-3 ml-auto">共 {list.length} 个备份</span>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-6 text-ink-3 text-xs">
          暂无备份
          <div className="text-[10px] text-ink-3 mt-1">点"立即备份"开始,或等 cron 每天 11:00 北京自动跑</div>
        </div>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {list.map(item => (
            <div key={item.key} className="flex items-center gap-2 bg-bg-elev/50 rounded p-2 text-[10px]">
              <div className="flex-1 min-w-0">
                <div className="font-mono truncate text-ink-1">{item.key}</div>
                <div className="text-ink-3 flex items-center gap-2 mt-0.5">
                  <span>{new Date(item.created_at).toLocaleString('zh-CN')}</span>
                  <span>·</span>
                  <span>{(item.size_bytes/1024).toFixed(1)}KB</span>
                  <span>·</span>
                  <span>{item.row_count} 行</span>
                  <span>·</span>
                  <span className={item.age_days > 25 ? 'text-amber-400' : ''}>{item.age_days}d 前</span>
                </div>
              </div>
              <button
                onClick={() => handleRestore(item.key)}
                disabled={restoring === item.key}
                className="px-2 py-1 rounded text-amber-400 hover:bg-amber-400/10 disabled:opacity-50"
                title="从该备份恢复(危险)"
              >
                {restoring === item.key ? <RefreshCw size={11} className="animate-spin" /> : '恢复'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================== 支付设置 ============================== */

function PaymentSection() {
  const [config, setConfig] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid_count: 0, paid_amount: 0 });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  async function load() {
    try {
      const c = await api.paymentConfig();
      if (c?.ok) {
        setConfig(c);
        setForm({
          method: c.active_method,
          wechat_mch_id: '',
          wechat_app_id: '',
          wechat_api_key: '',
          wechat_private_key: '',
          wechat_serial_no: '',
          alipay_app_id: '',
          alipay_private_key: '',
          alipay_public_key: '',
        });
      }
      const o = await api.paymentOrders({ limit: 10 });
      if (o?.ok) {
        setOrders(o.orders || []);
        setStats(o.stats || {});
      }
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    if (!confirm('确认保存支付配置?\n\n生产环境请确认所有密钥准确,避免资金损失')) return;
    setSaving(true);
    try {
      // 只发有值的字段
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v && v.length > 0) payload[k] = v;
      }
      const r = await api.paymentSaveConfig(payload);
      if (r?.ok) {
        toast.success(r.message);
        setEditing(false);
        load();
      } else {
        toast.error(r?.error || '保存失败');
      }
    } catch (e) {
      toast.error('保存失败: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  const methods = config?.available_methods || [];

  return (
    <div id="section-payment" className="card !p-3">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-brand-gold/10 flex items-center justify-center">
            <CreditCard size={12} className="text-brand-gold" />
          </div>
          <div>
            <h3 className="text-xs font-semibold">支付通道</h3>
            <div className="text-[10px] text-ink-3 mt-0.5">
              {config?.active_method === 'mock' ? (
                <span className="text-yellow-400">🧪 测试通道</span>
              ) : (
                <span className="text-green-400">✅ 真实通道:{methods.find(m => m.id === config?.active_method)?.name}</span>
              )}
              {' · '}已付 {stats.paid_count} 单 / 累计 ¥{stats.paid_amount.toFixed(2)}
            </div>
          </div>
        </div>
        <button
          onClick={() => setEditing(!editing)}
          className="text-xs text-brand-cyan hover:underline"
        >
          {editing ? '取消' : '配置 →'}
        </button>
      </div>

      {/* 支付方式状态 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        {methods.map(m => (
          <div
            key={m.id}
            className={`p-2 rounded-lg border text-[10px] ${
              config?.active_method === m.id
                ? 'bg-brand-gold/10 border-brand-gold/50'
                : m.enabled
                ? 'bg-green-500/5 border-green-500/30'
                : 'bg-white/3 border-white/10'
            }`}
          >
            <div className="font-medium flex items-center gap-1">
              {m.enabled ? '✅' : '⭕'} {m.name}
            </div>
            <div className="text-ink-3 mt-0.5">{m.desc}</div>
            {config?.active_method === m.id && (
              <div className="text-brand-gold mt-1 font-bold">当前激活</div>
            )}
          </div>
        ))}
      </div>

      {/* 编辑表单 */}
      {editing && (
        <div className="space-y-3 mt-3 p-3 bg-white/3 rounded-lg">
          <div>
            <label className="text-[10px] text-ink-3 mb-1 block">激活的支付方式</label>
            <select
              value={form.method || 'mock'}
              onChange={e => setForm({ ...form, method: e.target.value })}
              className="w-full px-2 py-1.5 bg-bg-base border border-white/10 rounded text-xs"
            >
              <option value="mock">🧪 Mock 测试</option>
              <option value="wechat_h5">微信 H5(手机微信内)</option>
              <option value="wechat_native">微信 Native(PC 扫码)</option>
              <option value="alipay_h5">支付宝 H5(手机浏览器)</option>
              <option value="alipay_face">支付宝 当面付(PC 扫码)</option>
            </select>
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer text-brand-cyan">📱 微信支付凭证(填了就启用)</summary>
            <div className="space-y-2 mt-2">
              <Input label="商户号 mch_id" v={form.wechat_mch_id} onChange={v => setForm({ ...form, wechat_mch_id: v })} />
              <Input label="AppID(公众号)" v={form.wechat_app_id} onChange={v => setForm({ ...form, wechat_app_id: v })} />
              <Input label="API v3 密钥(32位)" v={form.wechat_api_key} onChange={v => setForm({ ...form, wechat_api_key: v })} type="password" />
              <Input label="商户私钥" v={form.wechat_private_key} onChange={v => setForm({ ...form, wechat_private_key: v })} type="password" />
              <Input label="证书序列号 serial_no" v={form.wechat_serial_no} onChange={v => setForm({ ...form, wechat_serial_no: v })} />
            </div>
          </details>

          <details className="text-xs">
            <summary className="cursor-pointer text-brand-cyan">💙 支付宝凭证(填了就启用)</summary>
            <div className="space-y-2 mt-2">
              <Input label="AppID" v={form.alipay_app_id} onChange={v => setForm({ ...form, alipay_app_id: v })} />
              <Input label="应用私钥(RSA2)" v={form.alipay_private_key} onChange={v => setForm({ ...form, alipay_private_key: v })} type="password" />
              <Input label="支付宝公钥(验签)" v={form.alipay_public_key} onChange={v => setForm({ ...form, alipay_public_key: v })} type="password" />
            </div>
          </details>

          <button
            onClick={save}
            disabled={saving}
            className="w-full py-2 bg-brand-gold text-black text-xs font-bold rounded-lg flex items-center justify-center gap-1"
          >
            {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
            保存支付配置
          </button>
          <div className="text-[10px] text-ink-3">
            💡 留空的字段不更新。改密钥后立即生效,无需重启。
          </div>
        </div>
      )}

      {/* 最近订单 */}
      {orders.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] text-ink-3 mb-1.5">最近 10 单</div>
          <div className="space-y-1">
            {orders.map(o => (
              <div key={o.id} className="flex items-center justify-between p-1.5 bg-white/3 rounded text-[10px]">
                <div className="flex items-center gap-2">
                  <span className={`px-1 rounded text-[9px] ${
                    o.status === 'paid' ? 'bg-green-500/20 text-green-300' :
                    o.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-red-500/20 text-red-300'
                  }`}>{o.status}</span>
                  <span className="font-mono">{o.id.slice(-12)}</span>
                  <span className="text-ink-3">{o.phone ? o.phone.slice(-4) : '匿名'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-ink-3">{o.tier}</span>
                  <span className="text-brand-gold font-bold">¥{o.amount}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, v, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-[10px] text-ink-3 mb-0.5 block">{label}</label>
      <input
        type={type}
        value={v || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={type === 'password' ? '已配置,留空保留' : '选填,留空保留原值'}
        className="w-full px-2 py-1 bg-bg-base border border-white/10 rounded text-xs"
      />
    </div>
  );
}
